import { AxiosError } from "axios"
import { AxiosService, Result } from "./AxiosService"


export type TeamInfo = {
    name: string
    url: string
    role: 'Admin' | 'Member'
}

type UserInfo = {
    teams: TeamInfo[]
    isAdmin: boolean
}

type Member = {
    userId: string
    userName: string
    role: 'Admin' | 'Member'
}
export type OrganizationOverview = {
    organizationName: string
    organizationUrl: string
    members: Member[]
}

export type Invite = {
    email: string
    status: "PENDING" | "DECLINED"
    sentAt: number
}

export type PersonalInvite = {
    organization: string
    url: string
    status: "PENDING" | "DECLINED"
    sentAt: number
}

const createOrganizationFailures = [
    "MaximumOrganizationCreated",
    "CanNotBeEmpty",
    "AlreadyTaken",
    "MinimumCharactersNotMet",
] as const

export type CreateOrganizationFailure = typeof createOrganizationFailures[number]

function isCreateOrganizationFailure(value: string): value is CreateOrganizationFailure {
    return createOrganizationFailures.includes(value as CreateOrganizationFailure)
}

export const OrganizationService = new (class {
    fetchOrganizations(userToken: string): Promise<UserInfo> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/user`, userToken)
    }

    createOrganization(userToken: string, name: string): Promise<Result<string, CreateOrganizationFailure>> {
        return AxiosService.postAuthResult(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/organization`, userToken, (error: AxiosError<string>) =>
            (error.response?.status === 400 && isCreateOrganizationFailure(error.response?.data)) ? error.response?.data : undefined, { name })
    }

    getPersonalInvites(userToken: string): Promise<(PersonalInvite)[]> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/invites`, userToken)
    }

    getOrganizationUserIsPartOf(userToken: string): Promise<(PersonalInvite)[]> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/invites`, userToken)
    }

    getInvites(userToken: string, urlOrgName: string): Promise<Invite[]> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${urlOrgName}/invites`, userToken)
    }

    sendInvite(userToken: string, urlOrgName: string, email: string): Promise<void> {
        return AxiosService.postAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${urlOrgName}/invites`, userToken, { email })
    }

    acceptInvite(userToken: string, organization: string): Promise<void> {
        return AxiosService.putAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/invites`, userToken, { action: "accept", organization })
    }

    declineInvite(userToken: string, organization: string): Promise<void> {
        return AxiosService.putAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/invites`, userToken, { action: "decline", organization })
    }

    acceptDeclinedInvite(userToken: string, organization: string): Promise<void> {
        return AxiosService.putAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/invites`, userToken, { action: "acceptDeclined", organization })
    }

    revokeInvite(userToken: string, urlOrgName: string, email: string): Promise<void> {
        return AxiosService.deleteAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${urlOrgName}/invites`, userToken, { email })
    }
})()


export const OrganizationAdminService = new (class {
    listMembers(userToken: string, organizationUrl: string): Promise<{ email?: string, uid: string }[]> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${organizationUrl}/members`, userToken)
    }

    getOrganization(userToken: string, organizationUrl: string): Promise<OrganizationOverview> {
        return AxiosService.getAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${organizationUrl}`, userToken)
    }
})()


export const OrganizationOwnerService = new (class {
    updateRole(userToken: string, organizationUrl: string, forMember: string, role: string): Promise<void> {
        return AxiosService.putAuth(`${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}/org/${organizationUrl}/members`, userToken, { forMember, role })
    }
})()
